import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getInvoices } from '@/modules/Invoice/services'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    getInvoiceListColumns,
    getInvoiceListFilterItems,
} from '../utils/invoice-list-config'
import { useEInvoiceAccess } from './use-e-invoice-access'

/**
 * Filters interface for Invoice List
 */
interface InvoiceFilters {
    invoice_status: string[]
    contract_role: string[]
    contract_direction: string[]
    product_group_name: string[]
    account_manager_codes: string[]
    invoice_apply_startdate: string[] // [from, to] range
    invoice_apply_startdate_to: string[] // form-only for daterangepicker UI
    invoice_apply_enddate: string[] // [from, to] range — synced with startdate for both-date filtering
    payment_request_status: string[] // 'none' = chưa gửi đề nghị thanh toán
    reconciliation_statement_status: string[] // 'none' = chưa gửi biên bản đối soát
    e_invoice_status: string[] // 'none' = chưa phát hành hoá đơn điện tử
}

/**
 * Business logic hook for Invoice List
 * Manages data fetching, pagination, sorting, and filtering
 */
export const useInvoiceList = () => {
    const { t } = useTranslation(['invoice'])
    // Cột / filter "HĐ điện tử": chỉ nhóm Kế toán / BOM / BOD / Admin
    const { canView: canViewEInvoice } = useEInvoiceAccess()

    // URL params for pagination, sorting and filters
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<InvoiceFilters>({
        defaults: { sort_by: 'invoice_id', sort_order: 'desc', page_size: 20 },
        filterDefaults: {
            invoice_status: [],
            contract_role: [],
            contract_direction: [],
            product_group_name: [],
            account_manager_codes: [],
            invoice_apply_startdate: [],
            invoice_apply_startdate_to: [],
            invoice_apply_enddate: [],
            e_invoice_status: [],
            payment_request_status: [],
            reconciliation_statement_status: [],
        },
    })

    // Form for filter controls
    const { control } = useForm<InvoiceFilters>({
        defaultValues: filters,
    })

    // Fetch invoices data
    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICES, queryParams],
        queryFn: () => getInvoices(queryParams),
        placeholderData: true,
    })

    // Memoized columns configuration
    // Filter out invoice_exporter_code column (only shown in Invoicable tab)
    const columns = useMemo(
        () =>
            getInvoiceListColumns(t, { showEInvoice: canViewEInvoice }).filter(
                (col) => col.key !== 'invoice_exporter_code'
            ),
        [t, canViewEInvoice]
    )

    // Memoized filter configuration
    const filterItems = useMemo(
        () =>
            getInvoiceListFilterItems(t, setFilter, {
                showEInvoice: canViewEInvoice,
            }),
        [t, setFilter, canViewEInvoice]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        invoices: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,

        // Params & Filters
        params,
        queryParams,
        searchInputProps,

        // Configs
        filters,
        control,
        setFilter,
        columns,
        filterItems,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,

        // Translation
        t,
    }
}
